import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save, Palette, PanelTop, Undo2, FileEdit, ImageIcon, Eye, EyeOff, LayoutList, Type, ArrowUp, ArrowDown, Megaphone, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import api from "./api";
import { invalidateSiteSettings, FONT_CHOICES } from "../hooks/siteSettings";

/* Site Builder (Aug 2026). One place to shape the public website:
   toggle menu tabs on/off, recolour the whole site, jump to the
   content & image editors. */

const AUBERGINE = "#3E1F46";
const PLUM = "#5B2A4F";
const GOLD = "#C9A268";
const CREAM = "#FBF6E9";
const EDGE = "#E8DFCB";

function SelectRow({ label, value, onChange, options, testid }) {
  return (
    <label className="text-xs">
      <span className="block font-semibold mb-1 text-[10px] tracking-[0.2em] uppercase opacity-70">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-9 px-2 border rounded bg-white text-sm" style={{ borderColor: "#E8DFCB" }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

const NAV_TABS = [
  { slug: "home",          label: "Home" },
  { slug: "store",         label: "Store" },
  { slug: "catalog",       label: "Catalog" },
  { slug: "mystery-boxes", label: "Mystery Boxes" },
  { slug: "private-pickings", label: "Private Pickings" },
  { slug: "fairitage",     label: "Fairitage Sale" },
  { slug: "our-farm",      label: "Our Farm" },
  { slug: "breeders-club", label: "Breeders Club" },
  { slug: "bloom-club",    label: "Bloom Club" },
];

const THEME_VARS = [
  { key: "dahlias-sage",      label: "Primary (header band, buttons)", def: "#87966B" },
  { key: "dahlias-sage-deep", label: "Primary deep (hovers, dividers)", def: "#6F7E55" },
  { key: "dahlias-ink",       label: "Text ink", def: "#2A3A22" },
  { key: "dahlias-cream",     label: "Page background", def: "#F4ECDC" },
  { key: "dahlias-forest",    label: "Deep accent (headings)", def: "#2F4A2A" },
  { key: "dahlias-orange",    label: "Highlight accent", def: "#E0884A" },
];

const PRESETS = [
  { name: "Heritage Sage (default)", theme: {} },
  { name: "Aubergine Plum", theme: { "dahlias-sage": "#7C5E8A", "dahlias-sage-deep": "#5A4565", "dahlias-ink": "#3E2346", "dahlias-forest": "#3E2346", "dahlias-orange": "#C9A268" } },
  { name: "Olive Grove", theme: { "dahlias-sage": "#5A6B3E", "dahlias-sage-deep": "#41502C", "dahlias-ink": "#2A331C", "dahlias-forest": "#3E4A2A", "dahlias-orange": "#C49247" } },
  { name: "Clay & Rust", theme: { "dahlias-sage": "#9B3A1F", "dahlias-sage-deep": "#7A2D18", "dahlias-ink": "#3C2A1E", "dahlias-forest": "#5C2E1A", "dahlias-orange": "#E0884A" } },
];

const HOME_SECTIONS = [
  { slug: "hero",          label: "Hero banner" },
  { slug: "announcements", label: "Announcements" },
  { slug: "gallery",       label: "Rotating gallery" },
  { slug: "seasons",       label: "Seasonal countdown" },
  { slug: "introduction",  label: "Welcome introduction" },
  { slug: "farm-life",     label: "Life on the Farm" },
  { slug: "bloom-club",    label: "Bloom Club teaser" },
  { slug: "breeders-bed",  label: "Breeders' Bed teaser" },
  { slug: "timeline",      label: "Heritage timeline" },
];

const DEFAULT_ANNOUNCEMENT = {
  enabled: true,
  text: "THE BLOOM FOUNDRY IS OPEN NOW!!!",
  link_url: "https://thebloomfoundry.nz",
};

const norm = (r) => ({
  nav: r.nav || {}, theme: r.theme || {}, nav_labels: r.nav_labels || {},
  home_sections: (Array.isArray(r.home_sections) && r.home_sections.length)
    ? r.home_sections : HOME_SECTIONS.map((s) => ({ slug: s.slug, visible: true })),
  typography: r.typography || {}, buttons: r.buttons || {},
  announcement: { ...DEFAULT_ANNOUNCEMENT, ...(r.announcement || {}) },
  sales: { paused: false, hide_store: false, hide_catalogue: false, message: "", ...(r.sales || {}) },
});

export default function ConsoleSiteBuilder() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState("");

  useEffect(() => {
    api.get("/site-settings").then((r) => { setSettings(norm(r.data)); setSavedSnapshot(JSON.stringify(norm(r.data))); })
      .catch(() => toast.error("Couldn't load site settings."));
  }, []);

  const [confirmPublish, setConfirmPublish] = useState(null);
  const save = async () => {
    const changed = changedKeys();
    if (!changed.length) { toast.info("Nothing to publish , no unsaved changes."); return; }
    setConfirmPublish(changed);
  };
  const publishNow = async () => {
    setConfirmPublish(null);
    setSaving(true);
    try {
      const r = await api.put("/console/site-settings", settings);
      setSettings(norm(r.data));
      setSavedSnapshot(JSON.stringify(norm(r.data)));
      invalidateSiteSettings();
      toast.success("Published. The public website now shows these settings.");
    } catch { toast.error("Couldn't save."); }
    finally { setSaving(false); }
  };

  const changedKeys = () => {
    try {
      const saved = savedSnapshot ? JSON.parse(savedSnapshot) : null;
      if (!saved) return [];
      return Object.keys(settings || {}).filter((k) => JSON.stringify(settings[k]) !== JSON.stringify(saved[k]));
    } catch { return []; }
  };
  const dirty = changedKeys().length > 0;

  const previewDraft = () => {
    localStorage.setItem("heritage-site-draft", JSON.stringify(settings));
    window.open("/dahlias?sitedraft=1", "_blank");
  };

  const revert = async () => {
    if (!window.confirm("Revert the live site to the previous published look?")) return;
    try {
      const r = await api.post("/console/site-settings/revert");
      setSettings(norm(r.data));
      invalidateSiteSettings();
      toast.success("Reverted to the previous published settings.");
    } catch { toast.error("Nothing to revert to yet."); }
  };

  if (!settings) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const navOn = (slug) => settings.nav[slug] !== false;
  const toggleNav = (slug) => setSettings((s) => ({ ...s, nav: { ...s.nav, [slug]: !navOn(slug) } }));
  const setVar = (key, val) => setSettings((s) => ({ ...s, theme: { ...s.theme, [key]: val } }));
  const applyPreset = (p) => setSettings((s) => ({ ...s, theme: { ...p.theme } }));

  return (
    <div data-testid="site-builder" className="space-y-6" style={{ color: AUBERGINE }}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Your public website</div>
          <h1 className="font-serif text-5xl mt-1 leading-tight">Site Builder</h1>
          <p className="italic mt-1.5 text-sm opacity-70 max-w-2xl">
            Edits here are a draft until you press Save &amp; publish. Preview draft opens the draft in a new tab without publishing anything.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={previewDraft} data-testid="site-builder-preview"
            className="h-11 px-5 rounded border text-sm inline-flex items-center gap-2" style={{ borderColor: PLUM, color: PLUM }}>
            <Eye className="w-4 h-4" /> Preview draft (not live)
          </button>
          <button type="button" onClick={revert} data-testid="site-builder-revert"
            className="h-11 px-4 rounded border text-sm inline-flex items-center gap-2" style={{ borderColor: GOLD, color: "#8A6A2F" }}>
            <Undo2 className="w-4 h-4" /> Revert
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="site-builder-save"
            className="h-11 px-6 rounded text-white text-sm inline-flex items-center gap-2" style={{ backgroundColor: PLUM }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save &amp; publish{dirty ? ` (${changedKeys().length} unsaved)` : ""}
          </button>
        </div>
      </header>

      <section className="bg-white border rounded-md p-5" style={{ borderColor: EDGE }} data-testid="site-builder-announcement">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>
          <Megaphone className="w-4 h-4" /> Landing-page rolling banner
        </div>
        <p className="text-xs italic opacity-65 mb-4">This slim message rolls beneath the Heritage logo. Change the wording or switch it off anytime.</p>
        <div className="grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] gap-3 items-end">
          <label className="h-10 px-3 inline-flex items-center gap-2 border rounded text-sm font-semibold" style={{ borderColor: EDGE }}>
            <input type="checkbox" checked={settings.announcement.enabled !== false}
              onChange={(event) => setSettings((state) => ({ ...state, announcement: { ...state.announcement, enabled: event.target.checked } }))}
              data-testid="site-announcement-enabled" /> Show banner
          </label>
          <label className="text-xs">
            <span className="block font-semibold mb-1 text-[10px] tracking-[0.2em] uppercase opacity-70">Message</span>
            <input value={settings.announcement.text || ""} maxLength={180}
              onChange={(event) => setSettings((state) => ({ ...state, announcement: { ...state.announcement, text: event.target.value } }))}
              data-testid="site-announcement-text" className="w-full h-10 px-3 border rounded bg-white text-sm" style={{ borderColor: EDGE }} />
          </label>
          <label className="text-xs">
            <span className="block font-semibold mb-1 text-[10px] tracking-[0.2em] uppercase opacity-70">Opens when clicked</span>
            <input value={settings.announcement.link_url || ""} type="url"
              onChange={(event) => setSettings((state) => ({ ...state, announcement: { ...state.announcement, link_url: event.target.value } }))}
              data-testid="site-announcement-link" className="w-full h-10 px-3 border rounded bg-white text-sm" style={{ borderColor: EDGE }} />
          </label>
        </div>
      </section>

      {/* Homepage sections */}
      <section className="bg-white border rounded-md p-5" style={{ borderColor: EDGE }} data-testid="site-builder-sections">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>
          <LayoutList className="w-4 h-4" /> Homepage sections
        </div>
        <p className="text-xs italic opacity-65 mb-3">Move sections up or down and hide the ones you don&apos;t want. Use Preview draft to check before publishing.</p>
        <ul className="space-y-1.5 max-w-xl">
          {settings.home_sections.map((s, i) => {
            const meta = HOME_SECTIONS.find((h) => h.slug === s.slug) || { label: s.slug };
            const move = (dir) => setSettings((st) => {
              const xs = [...st.home_sections];
              const j = i + dir;
              if (j < 0 || j >= xs.length) return st;
              [xs[i], xs[j]] = [xs[j], xs[i]];
              return { ...st, home_sections: xs };
            });
            const toggleVis = () => setSettings((st) => {
              const xs = st.home_sections.map((x, k) => k === i ? { ...x, visible: x.visible === false } : x);
              return { ...st, home_sections: xs };
            });
            const on = s.visible !== false;
            return (
              <li key={s.slug} className="flex items-center gap-2 border rounded px-3 py-2" style={{ borderColor: EDGE, backgroundColor: on ? "#fff" : CREAM }} data-testid={`home-section-${s.slug}`}>
                <span className={`flex-1 text-sm font-semibold ${on ? "" : "line-through opacity-50"}`}>{meta.label}</span>
                <button type="button" title="Move up" aria-label="Move up" onClick={() => move(-1)} disabled={i === 0} data-testid={`section-up-${s.slug}`} className="px-1.5 py-1 rounded hover:bg-black/5 disabled:opacity-25 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em]"><ArrowUp className="w-4 h-4" /><span className="hidden md:inline">Move up</span></button>
                <button type="button" title="Move down" aria-label="Move down" onClick={() => move(1)} disabled={i === settings.home_sections.length - 1} data-testid={`section-down-${s.slug}`} className="px-1.5 py-1 rounded hover:bg-black/5 disabled:opacity-25 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em]"><ArrowDown className="w-4 h-4" /><span className="hidden md:inline">Move down</span></button>
                <button type="button" title={on ? "Hide" : "Show"} aria-label={on ? "Hide" : "Show"} onClick={toggleVis} data-testid={`section-toggle-${s.slug}`} className="px-1.5 py-1 rounded hover:bg-black/5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em]">
                  {on ? <Eye className="w-4 h-4" style={{ color: PLUM }} /> : <EyeOff className="w-4 h-4 opacity-50" />}<span className="hidden md:inline">{on ? "Hide" : "Show"}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Typography & buttons */}
      <section className="bg-white border rounded-md p-5" style={{ borderColor: EDGE }} data-testid="site-builder-typography">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold mb-3" style={{ color: PLUM }}>
          <Type className="w-4 h-4" /> Typography &amp; buttons
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <SelectRow label="Heading font" value={settings.typography.heading_font || ""} testid="typo-heading-font"
            onChange={(v) => setSettings((st) => ({ ...st, typography: { ...st.typography, heading_font: v } }))}
            options={[["", "Site default"], ...FONT_CHOICES.map((f) => [f, f])]} />
          <SelectRow label="Body font" value={settings.typography.body_font || ""} testid="typo-body-font"
            onChange={(v) => setSettings((st) => ({ ...st, typography: { ...st.typography, body_font: v } }))}
            options={[["", "Site default"], ...FONT_CHOICES.map((f) => [f, f])]} />
          <SelectRow label="Base text size" value={settings.typography.base_size || ""} testid="typo-base-size"
            onChange={(v) => setSettings((st) => ({ ...st, typography: { ...st.typography, base_size: v } }))}
            options={[["", "Default"], ["15", "Cosy (15px)"], ["16", "Standard (16px)"], ["17", "Comfy (17px)"], ["18", "Large (18px)"]]} />
          <SelectRow label="Heading weight" value={settings.typography.heading_weight || ""} testid="typo-heading-weight"
            onChange={(v) => setSettings((st) => ({ ...st, typography: { ...st.typography, heading_weight: v } }))}
            options={[["", "Default"], ["400", "Light"], ["500", "Medium"], ["600", "Semibold"], ["700", "Bold"]]} />
          <SelectRow label="Button shape" value={settings.buttons.shape || ""} testid="btn-shape"
            onChange={(v) => setSettings((st) => ({ ...st, buttons: { ...st.buttons, shape: v } }))}
            options={[["", "Default"], ["pill", "Pill"], ["rounded", "Rounded"], ["sharp", "Sharp"]]} />
          <SelectRow label="Button size" value={settings.buttons.size || ""} testid="btn-size"
            onChange={(v) => setSettings((st) => ({ ...st, buttons: { ...st.buttons, size: v } }))}
            options={[["", "Default"], ["sm", "Compact"], ["md", "Standard"], ["lg", "Generous"]]} />
        </div>
      </section>

      {confirmPublish && (
        <div data-testid="publish-confirm" className="border-2 rounded-md p-4 bg-white" style={{ borderColor: "#A33030" }}>
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: "#A33030" }}>Publish to the live website?</div>
          <p className="text-sm mt-1">This makes live: <strong>{confirmPublish.join(", ")}</strong>. Preview draft never publishes , only this does.</p>
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={publishNow} data-testid="publish-confirm-yes" className="h-9 px-4 rounded text-white text-xs tracking-[0.2em] uppercase font-semibold" style={{ backgroundColor: PLUM }}>Publish now</button>
            <button type="button" onClick={() => setConfirmPublish(null)} data-testid="publish-confirm-cancel" className="h-9 px-4 rounded border text-xs tracking-[0.2em] uppercase font-semibold" style={{ borderColor: EDGE }}>Keep editing</button>
          </div>
        </div>
      )}

      {/* Sales & visibility controls */}
      <section className="bg-white border rounded-md p-5" style={{ borderColor: EDGE }} data-testid="site-builder-sales">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>
          <PauseCircle className="w-4 h-4" /> Sales & visibility
        </div>
        <p className="text-xs italic opacity-65 mb-3">Reversible. Pausing blocks checkout and ticket purchases (including direct URLs) and hides the Store tab; nothing is deleted. Customer HQ, existing orders and Admin keep working.</p>
        <div className="grid gap-2 sm:grid-cols-3">
          {[["paused", "Pause new sales"], ["hide_store", "Hide Store"], ["hide_catalogue", "Hide Catalogue"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm rounded border px-3 py-2" style={{ borderColor: settings.sales[k] ? "#A33030" : EDGE }} data-testid={`site-sales-${k}`}>
              <input type="checkbox" checked={!!settings.sales[k]} onChange={(e) => setSettings((s) => ({ ...s, sales: { ...s.sales, [k]: e.target.checked } }))} />
              <span className={settings.sales[k] ? "font-semibold text-[#A33030]" : ""}>{label}</span>
            </label>
          ))}
        </div>
        <input value={settings.sales.message || ""} onChange={(e) => setSettings((s) => ({ ...s, sales: { ...s.sales, message: e.target.value } }))}
          placeholder="Message shown to customers while paused (optional)" className="mt-2 w-full h-9 px-2 border rounded text-sm" style={{ borderColor: EDGE }} data-testid="site-sales-message" />
      </section>

      {/* Menu tabs */}
      <section className="bg-white border rounded-md p-5" style={{ borderColor: EDGE }} data-testid="site-builder-nav">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>
          <PanelTop className="w-4 h-4" /> Menu tabs
        </div>
        <p className="text-xs italic opacity-65 mb-3">Switch a tab off to hide it from the public menu (the page itself stays reachable by direct link).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {NAV_TABS.map((tab) => {
            const on = navOn(tab.slug);
            return (
              <div key={tab.slug} className="rounded border" style={on ? { borderColor: PLUM } : { borderColor: EDGE }}>
                <button type="button" onClick={() => toggleNav(tab.slug)} data-testid={`site-nav-${tab.slug}`}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 text-sm font-semibold transition-colors"
                  style={on ? { backgroundColor: "#F2EAF3", color: PLUM } : { backgroundColor: CREAM, color: "#9C8B73" }}>
                  <span className={on ? "" : "line-through"}>{tab.label}</span>
                  {on ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                {on && (
                  <input
                    value={settings.nav_labels[tab.slug] || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, nav_labels: { ...s.nav_labels, [tab.slug]: e.target.value } }))}
                    placeholder={`Rename "${tab.label}"…`}
                    data-testid={`site-nav-label-${tab.slug}`}
                    className="w-full px-3 py-1.5 text-xs border-t bg-white outline-none"
                    style={{ borderColor: EDGE }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Colour theme */}
      <section className="bg-white border rounded-md p-5" style={{ borderColor: EDGE }} data-testid="site-builder-theme">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>
          <Palette className="w-4 h-4" /> Colour theme
        </div>
        <p className="text-xs italic opacity-65 mb-3">Pick a preset or fine-tune each colour. Applies across every public page.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map((p) => (
            <button key={p.name} type="button" onClick={() => applyPreset(p)} data-testid={`site-preset-${p.name.split(" ")[0].toLowerCase()}`}
              className="px-3 py-1.5 rounded-full border text-xs font-semibold inline-flex items-center gap-2"
              style={{ borderColor: EDGE, color: AUBERGINE }}>
              <span className="flex -space-x-1">
                {["dahlias-sage", "dahlias-forest", "dahlias-orange"].map((k) => (
                  <span key={k} className="w-3.5 h-3.5 rounded-full border border-white" style={{ backgroundColor: p.theme[k] || THEME_VARS.find((v) => v.key === k)?.def }} />
                ))}
              </span>
              {p.name}
            </button>
          ))}
          <button type="button" onClick={() => applyPreset(PRESETS[0])} data-testid="site-theme-reset"
            className="px-3 py-1.5 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5"
            style={{ borderColor: GOLD, color: "#8A6A2F" }}>
            <Undo2 className="w-3.5 h-3.5" /> Reset to defaults
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {THEME_VARS.map((v) => (
            <label key={v.key} className="flex items-center gap-3 border rounded p-2.5" style={{ borderColor: EDGE }}>
              <input type="color" value={settings.theme[v.key] || v.def}
                onChange={(e) => setVar(v.key, e.target.value)}
                data-testid={`site-colour-${v.key}`}
                className="w-9 h-9 rounded cursor-pointer border-0 bg-transparent" />
              <span className="text-xs">
                <span className="block font-semibold">{v.label}</span>
                <span className="opacity-50">{settings.theme[v.key] || `${v.def} (default)`}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      {/* Content & uploads */}
      <section className="bg-white border rounded-md p-5" style={{ borderColor: EDGE }} data-testid="site-builder-content">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>
          <ImageIcon className="w-4 h-4" /> Words &amp; photos
        </div>
        <p className="text-xs italic opacity-65 mb-3">
          Every heading, paragraph and photo slot on the public site is editable, including image uploads, in the content editor.
        </p>
        <Link to="/admin/content" data-testid="site-builder-open-content"
          className="inline-flex items-center gap-2 h-10 px-5 rounded border text-xs tracking-[0.2em] uppercase font-semibold"
          style={{ borderColor: PLUM, color: PLUM }}>
          <FileEdit className="w-4 h-4" /> Open the content &amp; photo editor
        </Link>
      </section>
    </div>
  );
}
